import { NavLink, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import type React from 'react';
import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { authContainer } from '@/modules/auth/di/container';

const navigation = [
  { name: 'Dados Pessoais', href: '/profile', icon: ProfileIcon },
  // `hasSubroutes`: `/platforms/:id/perks` é filha desta seção, e o item precisa
  // seguir destacado quando a pessoa está nos acessos de um perfil.
  {
    name: 'Minhas Plataformas',
    href: '/platforms',
    icon: PlatformsIcon,
    hasSubroutes: true,
  },
  { name: 'Segurança da Conta', href: '/security', icon: ShieldIcon },
  { name: 'Configurações', href: '/settings', icon: SettingsIcon },
] as const;

interface SidebarProps {
  isCollapsed: boolean;
  isSmScreen: boolean;
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
}

export function Sidebar({
  isCollapsed,
  isSmScreen,
  isDrawerOpen,
  onCloseDrawer,
}: SidebarProps) {
  const { clearAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    void authContainer.repository.clearAuth();
    clearAuth();
    void navigate('/sign-in');
  }, [clearAuth, navigate]);

  if (isSmScreen) {
    return (
      <>
        <div
          className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${
            isDrawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          onClick={onCloseDrawer}
        />
        <aside
          className={`border-(--color-border) bg-(--color-bg-primary) fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r shadow-xl transition-transform duration-300 ${
            isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarContent collapsed={false} onLogout={handleLogout} />
        </aside>
      </>
    );
  }

  return (
    <aside
      className={`border-(--color-border) bg-(--color-bg-primary) flex flex-col border-r transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <SidebarContent collapsed={isCollapsed} onLogout={handleLogout} />
    </aside>
  );
}

interface SidebarContentProps {
  collapsed: boolean;
  onLogout: () => void;
}

function NavItem({
  item,
  collapsed,
}: {
  item: {
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    hasSubroutes?: boolean;
  };
  collapsed: boolean;
}) {
  return (
    <NavLink
      to={item.href}
      end={!item.hasSubroutes}
      title={collapsed ? item.name : undefined}
      className={({ isActive }) =>
        `flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-(--color-accent)/10 text-(--color-accent)'
            : 'text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary)'
        }`
      }
    >
      <item.icon className="h-5 w-5 shrink-0" />
      <span
        className={`whitespace-nowrap transition-all duration-300 ${
          collapsed ? 'w-0 opacity-0' : 'opacity-100'
        }`}
      >
        {item.name}
      </span>
    </NavLink>
  );
}

function SidebarContent({ collapsed, onLogout }: SidebarContentProps) {
  return (
    <>
      <div
        data-sidebar="group-label"
        className={`text-(--color-text-secondary) flex shrink-0 items-center overflow-hidden rounded-md px-4 text-xs font-medium outline-none transition-all duration-300 ${
          collapsed ? 'h-0 select-none opacity-0' : 'h-8 opacity-100'
        }`}
      >
        Menu
      </div>

      <nav className="flex flex-1 flex-col px-2 py-4">
        <div className="flex-1 space-y-4">
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavItem key={item.name} item={item} collapsed={collapsed} />
            ))}
          </div>
        </div>

        <div className="border-(--color-border) border-t pt-3">
          <button
            onClick={onLogout}
            title={collapsed ? 'Sair' : undefined}
            className="flex w-full items-center gap-3 overflow-hidden rounded-lg px-3 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50"
            aria-label="Sair"
          >
            <svg
              className="h-5 w-5 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
              />
            </svg>
            <span
              className={`whitespace-nowrap transition-all duration-300 ${
                collapsed ? 'w-0 opacity-0' : 'opacity-100'
              }`}
            >
              Sair
            </span>
          </button>
        </div>
      </nav>
    </>
  );
}

function ProfileIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
      />
    </svg>
  );
}

function PlatformsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z"
      />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
      />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  );
}
